import React, { useState, Fragment, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadMatterCD } from "../../action/matterCD";
import { setAlert } from "../../action/alert";
import Alert from "../layout/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import { LabelText } from "../Common/Typography/AppTypography";
import { selectMenuProps } from "../../utils/selectMenuProps";

const formControlSx = {
  width: "100%",
  maxWidth: { xs: "100%", md: 360 },
};

function UploadCDForm({ activeTab }) {
  const allowedDeviceCategories =
    useSelector((state) => state.auth.allowedDeviceCategories) || [];
  const dispatch = useDispatch();
  const [csvProductIds, setCsvProductIds] = useState([]);
  const [fwVersion, setFwVersion] = useState("");
  const [certificate, setCertificate] = useState("");
  const [category, setCategory] = useState("");
  const [cdFile, setCdFile] = useState(null);
  const [error, setError] = useState("");
  const [fwVersionError, setFwVersionError] = useState("");
  const [loading, setLoading] = useState(false);
  const csvInputRef = useRef(null);
  const cdInputRef = useRef(null);

  const resetForm = () => {
    setFwVersion("");
    setCertificate("");
    setCategory("");
    setCsvProductIds([]);
    setCdFile(null);
    setError("");
    setFwVersionError("");
    setLoading(false);
    if (csvInputRef.current) csvInputRef.current.value = "";
    if (cdInputRef.current) cdInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFwVersionError("");
    if (!cdFile) {
      setError("Please select a CD file to upload.");
      return;
    }
    if (!/^\d+\.\d+$/.test(fwVersion)) {
      setFwVersionError(
        "Firmware Version must be a decimal value (e.g. 1.0, 2.5)"
      );
      return;
    }
    setError("");
    setLoading(true);

    const formData = new FormData();
    formData.append("cd", cdFile);
    formData.append("category", category);
    formData.append("fwVersion", fwVersion);
    formData.append("productIds", csvProductIds.join(","));
    try {
      await uploadMatterCD(formData);
      dispatch(setAlert("CDs uploaded successfully!", "success"));
      resetForm();
    } catch (err) {
      let errMsg = err.response?.data?.message || err.message;
      if (
        typeof errMsg === "string" &&
        errMsg.toLowerCase().includes("already exist")
      ) {
        errMsg = "A record with these details already exists.";
      }
      dispatch(setAlert(errMsg, "danger"));
      setError("");
    }
    setLoading(false);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      dispatch(
        setAlert(
          "Invalid file type. Please upload a '.csv' file containing Product IDs.",
          "danger"
        )
      );
      setCsvProductIds([]);
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = function (event) {
      const text = event.target.result;
      const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      const ids = lines.slice(1);
      setCsvProductIds(ids);
    };
    reader.readAsText(file);
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <Alert />
      <form onSubmit={handleSubmit}>
        {/* Upload CSV */}
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Upload CSV of Product IDs:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <Box sx={formControlSx}>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 8,
                  border: "1px solid rgba(0,0,0,0.42)",
                  borderRadius: 4,
                }}
              />
              {csvProductIds.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
                  <strong>Total Product IDs:</strong> {csvProductIds.length}
                </Typography>
              )}
            </Box>
          </Grid2>
        </Grid2>

        {/* Category */}
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Category:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Select Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={allowedDeviceCategories.map((cat) => ({
                value: cat,
                label: cat,
              }))}
              MenuProps={selectMenuProps}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        {/* Firmware Version */}
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Firmware Version:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Firmware Version"
              value={fwVersion}
              onChange={(e) => setFwVersion(e.target.value)}
              sx={formControlSx}
            />
            {fwVersionError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {fwVersionError}
              </Typography>
            )}
          </Grid2>
        </Grid2>

        {/* CD File */}
        <Grid2 container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Certificate (CD File):</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <Box sx={formControlSx}>
              <input
                ref={cdInputRef}
                type="file"
                accept=".pem,.crt,.cer,.der,.p7b,.p7c,.key,.txt"
                onChange={(e) => setCdFile(e.target.files[0])}
                required
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 8,
                  border: "1px solid rgba(0,0,0,0.42)",
                  borderRadius: 4,
                }}
              />
            </Box>
          </Grid2>
        </Grid2>

        {/* Buttons */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={loading}
          >
            Upload CD
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </form>
    </Paper>
  );
}

export default UploadCDForm;